/**
 * One data-fetching primitive for every read-only API call in the SPA.
 *
 * The v4 review (docs/v4/06 TD4) flagged that screens fetched ad-hoc in
 * `useEffect` with divergent loading/error handling. Every per-domain hook
 * (`useFuelPlan`, `useDashboard`, …) is built on this so the pattern can't
 * re-drift. Mutations stay in the typed `lib/*Api.ts` wrappers.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { isAbortError, userFacingError } from "./api";

export type ApiResource<T> = {
  data: T | null;
  loading: boolean;
  error: string | null;
  /** Re-run the fetcher. */
  refetch: () => void;
};

/**
 * Runs `fetcher` on mount and whenever `deps` change; aborts in-flight
 * requests on unmount / dep change. `fetcher` receives an `AbortSignal`.
 */
export function useApiResource<T>(
  fetcher: (signal: AbortSignal) => Promise<T>,
  deps: ReadonlyArray<unknown>
): ApiResource<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  // Keep the latest fetcher without making it a dep — `deps` is the contract.
  // Callers recreate `fetcher` every render (it closes over component scope),
  // so depending on it would refetch on every render. The ref lets the effect
  // always call the current fetcher while only `deps`/`nonce` retrigger it.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  // The fetch effect must re-run when the caller-supplied `deps` change (the
  // public refetch contract) or when `refetch` bumps `nonce`. `deps` has a
  // dynamic length, so it can't be an array literal in the effect's dep list.
  // Fold it (plus `nonce`) into a single stable token instead: `useMemo`
  // recomputes the token only when one of those values changes, so the effect
  // below can depend on the literal `[fetchToken]` — a complete, honest
  // dependency list that exhaustive-deps can verify, with no suppression.
  const fetchToken = useMemo(
    () => ({ deps, nonce }),
    // The token's identity is exactly "deps changed or nonce changed".
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [...deps, nonce]
  );

  // Identity changes only when `deps` change — NOT on a `refetch()` nonce bump.
  // Same `[...deps]` spread pattern as `fetchToken` above.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const depsToken = useMemo(() => deps, [...deps]);

  // When the resource identity changes — e.g. navigating `/plan/:a` → `/plan/:b`
  // — clear stale data so the screen shows its loading state instead of the
  // previous resource's content for a beat. A plain `refetch()` (deps unchanged)
  // does NOT clear, so a manual refresh of the same resource never flashes
  // empty. On first mount `setData(null)` is a no-op (data is already null).
  useEffect(() => {
    setData(null);
  }, [depsToken]);

  useEffect(() => {
    const ctrl = new AbortController();
    // Ignore late completions from a superseded run (Strict Mode remount, fast
    // navigation, refetch) so an aborted fetch can't clear `loading` while a
    // newer request is still in flight — that flash looked like "not ready yet".
    let active = true;
    setLoading(true);
    setError(null);
    fetcherRef.current(ctrl.signal)
      .then((d) => {
        if (active) setData(d);
      })
      .catch((e: unknown) => {
        if (!active || isAbortError(e)) return;
        setError(userFacingError(e, "We couldn't load that. Try again in a moment."));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
      ctrl.abort();
    };
  }, [fetchToken]);

  const refetch = useCallback(() => setNonce((n) => n + 1), []);

  return { data, loading, error, refetch };
}
